import React from 'react'
import { BaseCardProps } from '../Card'
import {
    isPublicationCardItemWithVideo,
    toLinkWithHint,
    useFeature,
} from '@news-mono/web-common'
import {
    Bullet,
    Teaser,
    TopicBylineWrapper,
    TopicLink,
    AuthorLink,
    CardContainer,
    StyledCardMedia,
    Author,
    Classification,
} from '../components'
import {
    StyledHeadline,
    StyledCardBodyWrapper,
    CardLinkWrapper,
    StyledReadMore,
} from './ListCard.styled'
import { getByline, getClassification, getTopicLink } from '../utils'
import { CommentsCount } from '../components/card-body/byline/CommentsCount'

export interface ListCardProps extends BaseCardProps<'list-card'> {
    canPlayVideoInline?: boolean
}

export const ListCard = ({
    item,
    onEvent,
    canPlayVideoInline,
    disableImageLazyLoad,
    imageFetchPriority,
}: Omit<ListCardProps, 'kind'>) => {
    const classificationEnabled = useFeature('story-classification')

    const { loaded: isLoading } = item
    const result = isLoading ? item.result : undefined
    const articleLink = toLinkWithHint(item)
    const byline = getByline(item)
    const topicLink = getTopicLink(item)
    const classification = getClassification(item, classificationEnabled)
    const isVideo = result && isPublicationCardItemWithVideo(result)

    return (
        <CardContainer>
            {isVideo && canPlayVideoInline ? (
                <ListCardMedia
                    item={item}
                    onEvent={onEvent}
                    canPlayVideoInline={canPlayVideoInline}
                    disableImageLazyLoad={disableImageLazyLoad}
                    imageFetchPriority={imageFetchPriority}
                />
            ) : (
                <CardLinkWrapper to={articleLink}>
                    <ListCardMedia
                        item={item}
                        onEvent={onEvent}
                        disableImageLazyLoad={disableImageLazyLoad}
                        imageFetchPriority={'high'}
                    />
                </CardLinkWrapper>
            )}
            {result && (
                <StyledCardBodyWrapper>
                    <TopicBylineWrapper>
                        {classification && (
                            <>
                                <Classification type={classification}>
                                    {classification}
                                </Classification>
                                <Bullet />
                            </>
                        )}
                        <TopicLink to={topicLink}>
                            {result.primaryTopic.title}
                        </TopicLink>
                        <Bullet />
                        {!byline.link ? (
                            <Author>{byline.text}</Author>
                        ) : (
                            <AuthorLink to={byline.link}>
                                {byline.text}
                            </AuthorLink>
                        )}
                        {result.allowCommenting && (
                            <>
                                <Bullet />
                                <CommentsCount articleUrl={result._self} />
                            </>
                        )}
                    </TopicBylineWrapper>
                    <CardLinkWrapper to={articleLink}>
                        <StyledHeadline>{result.shortHeadline}</StyledHeadline>
                        <Teaser>{result.teaser}</Teaser>
                        <StyledReadMore readTime={result.readTimeMinutes} />
                    </CardLinkWrapper>
                </StyledCardBodyWrapper>
            )}
        </CardContainer>
    )
}

type ListCardMediaProps = Pick<
    BaseCardProps<any>,
    'onEvent' | 'item' | 'disableImageLazyLoad' | 'imageFetchPriority'
> & {
    canPlayVideoInline?: boolean
}

export const ListCardMedia = ({
    item,
    onEvent,
    canPlayVideoInline,
    disableImageLazyLoad,
    imageFetchPriority,
}: ListCardMediaProps) => {
    return (
        <StyledCardMedia
            fixedRatio={{
                default: '4:3',
                sm: canPlayVideoInline ? '16:9' : undefined,
            }}
            CSSCrop={{
                default: {
                    width: 770,
                    height: 577,
                },
            }}
            imageWidths={{
                desktop: '100%',
                tablet: '100%',
                mobile: '100%',
                fallbackWidth: 770,
            }}
            item={item}
            onEvent={onEvent}
            disableImageLazyLoad={disableImageLazyLoad}
            imageFetchPriority={imageFetchPriority}
            mediaMode={'fullHeight'}
            isLarge={canPlayVideoInline}
            willPlayVideoInline={canPlayVideoInline}
        />
    )
}
